/**
 * Service Worker Update Manager
 * 
 * Handles safe service worker updates:
 * - Detects new service worker versions
 * - Waits for full installation before reloading
 * - Prevents reload loops
 * - Preserves runtime caches
 * - Only reloads when app is stable
 */

class ServiceWorkerUpdateManager {
  constructor() {
    this.registration = null;
    this.updateCheckInterval = null;
    this.isReloading = false;
    this.reloadAttempts = 0;
    this.maxReloadAttempts = 3;
    this.updateCheckIntervalMs = 60000; // Check every minute
    this.stabilityCheckDelay = 2000; // Wait 2 seconds for app stability
    this.hadControllerOnLoad = false;
    this.pendingUpdate = false;
  }

  /**
   * Initialize service worker update checking
   */
  async initialize() {
    if (!('serviceWorker' in navigator)) {
      console.log('[SW Update] Service Workers not supported');
      return;
    }

    try {
      // Track whether this page load was already controlled (helps avoid reloading on first install)
      this.hadControllerOnLoad = !!navigator.serviceWorker.controller;

      // Register service worker
      this.registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
        updateViaCache: 'none' // Always check for updates
      });

      console.log('[SW Update] Service Worker registered');

      // If an update was already downloaded earlier and is waiting, activate it immediately.
      // This is a common case for "installed PWAs" that haven't been fully closed recently.
      if (this.registration.waiting && navigator.serviceWorker.controller) {
        console.log('[SW Update] Found waiting service worker on init - activating now');
        this.pendingUpdate = true;
        this.activateNewWorker(this.registration.waiting);
      }

      // Check for updates immediately
      await this.checkForUpdates();

      // Listen for service worker updates
      this.registration.addEventListener('updatefound', () => {
        this.handleUpdateFound();
      });

      // Listen for controller changes (when new SW takes control)
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        this.handleControllerChange();
      });

      // Periodic update checks
      this.startPeriodicUpdateCheck();

      // Check for updates when page becomes visible
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
          this.checkForUpdates();
        }
      });

    } catch (error) {
      console.error('[SW Update] Service Worker registration failed:', error);
    }
  }

  /**
   * Check for service worker updates
   */
  async checkForUpdates() {
    if (!this.registration) {
      return;
    }

    try {
      await this.registration.update();
      console.log('[SW Update] Update check completed');
    } catch (error) {
      console.error('[SW Update] Error checking for updates:', error);
    }
  }

  /**
   * Handle when a new service worker is found
   */
  handleUpdateFound() {
    const newWorker = this.registration.installing || this.registration.waiting;

    if (!newWorker) {
      return;
    }

    console.log('[SW Update] New service worker found, state:', newWorker.state);

    // Track state changes
    newWorker.addEventListener('statechange', () => {
      console.log('[SW Update] New worker state changed to:', newWorker.state);

      if (newWorker.state === 'installed') {
        // New service worker is installed but waiting
        if (navigator.serviceWorker.controller) {
          // There's an active service worker, so this is an update
          console.log('[SW Update] New service worker installed and waiting');
          // IMPORTANT:
          // Do NOT wait for "activated" before sending SKIP_WAITING.
          // Waiting for "activated" first causes a deadlock where updates never apply until all tabs close.
          this.pendingUpdate = true;
          this.activateNewWorker(newWorker);
        } else {
          // First time installation
          console.log('[SW Update] Service worker installed for the first time');
        }
      }

      if (newWorker.state === 'activated') {
        console.log('[SW Update] New service worker activated');
      }
    });
  }

  /**
   * Handle when a new worker is ready
   */
  async handleNewWorkerReady(newWorker) {
    // Prevent multiple activation attempts
    if (this.isReloading) {
      console.log('[SW Update] Already processing an update, skipping');
      return;
    }

    // Legacy method kept for compatibility; activation is now triggered directly from "installed" state.
    this.pendingUpdate = true;
    this.activateNewWorker(newWorker);
  }

  /**
   * Wait for service worker to be fully ready
   */
  async waitForWorkerReady(worker) {
    return new Promise((resolve) => {
      if (worker.state === 'activated') {
        resolve();
        return;
      }

      const checkState = () => {
        if (worker.state === 'activated') {
          resolve();
        } else {
          setTimeout(checkState, 100);
        }
      };

      checkState();
    });
  }

  /**
   * Check if the app is in a stable state (not loading, no pending requests)
   */
  isAppStable() {
    // Check if document is ready
    if (document.readyState !== 'complete') {
      return false;
    }

    // Check if there are pending fetch requests (basic check)
    // In a real app, you might want to track active requests
    return true;
  }

  /**
   * Activate the new service worker and reload
   */
  async activateNewWorker(newWorker) {
    if (this.isReloading) {
      console.log('[SW Update] Reload already in progress');
      return;
    }

    if (this.reloadAttempts >= this.maxReloadAttempts) {
      console.warn('[SW Update] Max reload attempts reached, stopping');
      return;
    }

    // Check if page is already unloading
    if (document.readyState === 'unload' || document.readyState === 'unloading') {
      console.log('[SW Update] Page already unloading, skipping reload');
      return;
    }

    this.isReloading = true;
    this.reloadAttempts++;

    console.log('[SW Update] Activating new service worker...');

    try {
      // Tell the new worker to skip waiting
      if (newWorker && newWorker.postMessage) {
        newWorker.postMessage({ type: 'SKIP_WAITING' });
      } else {
        // Fallback: send message to any waiting worker
        if (this.registration && this.registration.waiting) {
          this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
      }

      // Wait a moment for the worker to activate
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Use a flag to prevent multiple reloads
      if (!window.__swReloading) {
        window.__swReloading = true;
        console.log('[SW Update] Reloading page to use new service worker');
        
        // Small delay to ensure message is sent
        setTimeout(() => {
          window.location.reload();
        }, 100);
      }
    } catch (error) {
      console.error('[SW Update] Error activating new worker:', error);
      this.isReloading = false;
      window.__swReloading = false;
    }
  }

  /**
   * Handle controller change (new SW has taken control)
   */
  handleControllerChange() {
    console.log('[SW Update] Service worker controller changed');

    // If this page was already controlled on load, a controllerchange means an update took over.
    // Force a one-time reload so the newest JS/CSS/assets load under the new SW.
    if (this.hadControllerOnLoad || this.pendingUpdate) {
      try {
        const alreadyReloaded = sessionStorage.getItem('__ulca_sw_reloaded__') === '1';
        if (!alreadyReloaded) {
          sessionStorage.setItem('__ulca_sw_reloaded__', '1');
          console.log('[SW Update] Reloading to apply updated service worker');
          window.location.reload();
          return;
        }
      } catch (e) {
        // If sessionStorage is unavailable, fall back to the existing reload protection flags.
      }
    }

    // Reset flags after a delay (in case reload didn't happen)
    setTimeout(() => {
      this.isReloading = false;
      this.pendingUpdate = false;
    }, 5000);
  }

  /**
   * Start periodic update checks
   */
  startPeriodicUpdateCheck() {
    // Clear any existing interval
    if (this.updateCheckInterval) {
      clearInterval(this.updateCheckInterval);
    }

    // Check for updates periodically
    this.updateCheckInterval = setInterval(() => {
      this.checkForUpdates();
    }, this.updateCheckIntervalMs);

    console.log(`[SW Update] Periodic update check started (every ${this.updateCheckIntervalMs / 1000}s)`);
  }

  /**
   * Stop periodic update checks
   */
  stopPeriodicUpdateCheck() {
    if (this.updateCheckInterval) {
      clearInterval(this.updateCheckInterval);
      this.updateCheckInterval = null;
    }
  }

  /**
   * Get current service worker registration
   */
  getRegistration() {
    return this.registration;
  }
}

// Create singleton instance
const serviceWorkerUpdateManager = new ServiceWorkerUpdateManager();

export default serviceWorkerUpdateManager;

